import React from 'react';
import { Alert, AlertTitle } from '@mui/material';

function FlattenArrayOfObjects(key) {
    return (prev, curr, index) => {
        if (curr instanceof Object)
            return {
                ...prev,
                ...Object.keys(curr).reduce((subPrev, subCurr) => {
                    return { ...subPrev, [`${key}-${index + 1}-${subCurr}`]: curr[subCurr] };
                }, {}),
            };

        return { ...prev, [`${key} - ${index + 1}`]: curr };
    };
}

/**
 * Recursively extract all error messages from a nested error object
 * @param {Object} errorObj - The error object (potentially nested)
 * @param {string} path - The current path for context
 * @returns {string[]} - Array of error messages
 */
function extractNestedErrors(errorObj, path = '') {
    const messages = [];

    if (!errorObj || typeof errorObj !== 'object') return messages;

    // If this object has a message, it's a leaf error
    if (errorObj.message) {
        const prefix = path ? `${path}: ` : '';
        messages.push(`${prefix}${errorObj.message}`);
        return messages;
    }

    // Otherwise, recurse into nested objects
    Object.keys(errorObj).forEach((key) => {
        const value = errorObj[key];
        const newPath = path ? `${path}.${key}` : key;

        if (value?.message) {
            // Leaf error with message
            messages.push(`${newPath}: ${value.message}`);
        } else if (value && typeof value === 'object') {
            // Nested object, recurse
            messages.push(...extractNestedErrors(value, newPath));
        }
    });

    return messages;
}

function FormattedArrayErrors(errors) {
    const errorListStyles = {
        maxHeight: '300px',
        overflowY: 'auto',
        margin: 0,
        padding: '0 20px',
        listStyleType: 'disc',
    };

    const formatKey = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

    if (errors instanceof Array)
        return (
            <ul style={errorListStyles}>
                {errors.map((item) => (
                    <li key={item}>{item}</li>
                ))}
            </ul>
        );
    if (errors instanceof Object)
        return (
            <ul style={errorListStyles}>
                {Object.keys(errors).map((key) => {
                    if (errors[key] instanceof Array) {
                        const first = errors[key][0];
                        if (first?.type === 'unique' && first?.message) {
                            return <li key={key}>{first.message}</li>;
                        }

                        return (
                            <li key={key}>
                                <strong>{formatKey(key)}:</strong> has the following errors:
                                {FormattedArrayErrors(
                                    errors[key].reduce(
                                        FlattenArrayOfObjects(key),
                                        {} //
                                    )
                                )}
                            </li>
                        );
                    }

                    // Check if it has a direct message
                    if (errors[key]?.message) {
                        return <li key={key}>{errors[key].message}</li>;
                    }

                    // For nested objects without a direct message, extract nested errors
                    const nestedErrors = extractNestedErrors(errors[key], key);
                    if (nestedErrors.length > 0) {
                        return (
                            <li key={key}>
                                <strong>{formatKey(key)}:</strong> has the following errors:
                                <ul style={{ ...errorListStyles, marginTop: '4px' }}>
                                    {nestedErrors.map((msg) => (
                                        <li key={`${key}-${msg}`}>{msg}</li>
                                    ))}
                                </ul>
                            </li>
                        );
                    }

                    // Fallback with more details
                    return (
                        <li key={key}>
                            Error with <strong>{formatKey(key)}</strong>: {JSON.stringify(errors[key], null, 2)}
                        </li>
                    );
                })}
            </ul>
        );

    return null;
}

export default function ControllerErrorAlert({ title, errors, footer, severity = 'error', variant, sx }) {
    if (errors == null || errors.length === 0 || Object.keys(errors).length === 0) return <></>;

    return (
        <Alert severity={severity} variant={variant} sx={{ marginY: '10px', width: '100%', ...sx }}>
            <AlertTitle>{title}</AlertTitle>
            {typeof errors === 'string' ? <div>{errors}</div> : FormattedArrayErrors(errors)}
            <AlertTitle>{footer}</AlertTitle>
        </Alert>
    );
}
